/**
 * Google Sheet Service
 *
 * Submits form data to a Google Apps Script Web App endpoint,
 * which then appends the data to a Google Sheet.
 *
 * Setup:
 * 1. Create a Google Sheet with headers matching the form fields
 * 2. Open Extensions → Apps Script
 * 3. Paste the provided Apps Script code
 * 4. Deploy as Web App (Execute as: Me, Access: Anyone)
 * 5. Copy the Web App URL into src/config.js
 */

const GOOGLE_SHEET_ENDPOINT = process.env.REACT_APP_GOOGLE_SHEET_ENDPOINT || '';

/**
 * Submit form data to Google Sheet via Apps Script Web App
 * @param {Object} formData - The form data object
 * @returns {Promise<Object>} - Response with success status
 */
export async function submitToSheet(formData) {
  if (!GOOGLE_SHEET_ENDPOINT) {
    console.warn('[GoogleSheet] No endpoint configured. Set REACT_APP_GOOGLE_SHEET_ENDPOINT');
    throw new Error('Google Sheet chưa được cấu hình. Vui lòng thử lại sau.');
  }

  try {
    await fetch(GOOGLE_SHEET_ENDPOINT, {
      method: 'POST',
      mode: 'no-cors', // Required for Google Apps Script CORS handling
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    // With no-cors, we can't read the response, so assume success
    return { success: true };
  } catch (error) {
    console.error('[GoogleSheet] Submission error:', error);
    throw new Error('Không thể gửi thông tin. Vui lòng kiểm tra kết nối và thử lại.');
  }
}

/**
 * Google Apps Script Code - Paste this in Extensions → Apps Script
 *
 * Copy and paste the code below into your Apps Script editor,
 * then deploy as Web App.
 */
export const APPS_SCRIPT_CODE = `/**
 * Google Apps Script - Nhà Trọ KTX Form Handler
 *
 * Deploy this as a Web App:
 * 1. Deploy → New Deployment → Web App
 * 2. Execute as: Me
 * 3. Who has access: Anyone
 * 4. Copy the Web App URL
 */

// Configure your Sheet ID and Sheet name here
const SHEET_ID = 'YOUR_GOOGLE_SHEET_ID_HERE';
const SHEET_NAME = 'FormSubmissions';

function doPost(e) {
  try {
    const data = JSON.parse(e.postData.contents);
    const sheet = SpreadsheetApp.openById(SHEET_ID).getSheetByName(SHEET_NAME);

    // If sheet doesn't exist, create it with headers
    if (!sheet) {
      const newSheet = SpreadsheetApp.openById(SHEET_ID).insertSheet(SHEET_NAME);
      newSheet.appendRow([
        'Timestamp',
        'Họ tên',
        'Số điện thoại',
        'Loại nhà trọ',
        'Địa điểm (Quận)',
        'Tiện ích mong muốn',
        'Ngày dự kiến đến',
        'Ghi chú'
      ]);
      newSheet.appendRow([
        new Date().toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }),
        data.name || '',
        data.phone || '',
        data.propertyType || '',
        data.district || '',
        data.amenities ? data.amenities.join(', ') : '',
        data.moveInDate || '',
        data.note || ''
      ]);
    } else {
      sheet.appendRow([
        new Date().toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }),
        data.name || '',
        data.phone || '',
        data.propertyType || '',
        data.district || '',
        data.amenities ? data.amenities.join(', ') : '',
        data.moveInDate || '',
        data.note || ''
      ]);
    }

    return ContentService
      .createTextOutput(JSON.stringify({ success: true }))
      .setMimeType(ContentService.MimeType.JSON);
  } catch (error) {
    return ContentService
      .createTextOutput(JSON.stringify({ success: false, error: error.toString() }))
      .setMimeType(ContentService.MimeType.JSON);
  }
}

function doGet() {
  return ContentService
    .createTextOutput(JSON.stringify({ status: 'OK', message: 'Nhà Trọ KTX Form Handler is running.' }))
    .setMimeType(ContentService.MimeType.JSON);
}
`;

const GoogleSheetService = {
  submitToSheet,
  APPS_SCRIPT_CODE,
};

export default GoogleSheetService;